import {chunk, flatten, partition, uniqBy} from 'lodash';

import {fetchRawBootInboxesAndChannelsAsync} from '../../../../../srcCommon/services/boot/fetch/bootFetchHelpers';
import {CancelToken} from '../../../../helpers/asyncCancelHelpers';
import {buildInboxSearchUrl} from '../../../../helpers/models/inboxHelpers';
import {isTeammateNamespace} from '../../../../helpers/models/namespaceHelpers';
import {ApiInboxes, InboxModel, parseInbox} from '../../../../models/api/inboxModel';
import {fetchApiAsync} from '../../../../services/http/http';

/*
 * Constants.
 */

const InboxesLimit = 50;

/*
 * Types.
 */

interface FetchAllProps {
  companyUrl: string;
  cancelToken: CancelToken;
}

interface PrefetchProps {
  companyUrl: string;
  namespace: string;
  cancelToken: CancelToken;
}

interface FetchProps {
  companyUrl: string;
  search?: string;
  namespace?: string;
  includePrivate?: boolean;
  cancelToken: CancelToken;
}

interface FetchIdsProps {
  companyUrl: string;
  ids: ReadonlyArray<number>;
  cancelToken: CancelToken;
}

/*
 * Service.
 */

export class InboxService {
  cache = new Map<string, Promise<ReadonlyArray<InboxModel>>>();

  byIdsCache = new Map<number, Promise<ReadonlyArray<InboxModel>>>();

  // TODO: Remove this. It's currently used for Rule Impact.
  fetchAll({companyUrl, cancelToken}: FetchAllProps): Promise<ReadonlyArray<InboxModel>> {
    const cacheItem = this.cache.get(companyUrl);
    if (cacheItem) {
      return cacheItem;
    }

    const request = fetchRawBootInboxesAndChannelsAsync(companyUrl, false, 0, null, cancelToken).then(
      (data) => data.rawInboxes.map(parseInbox),
    );

    request.catch(() => this.cache.delete(companyUrl));
    this.cache.set(companyUrl, request);
    return request;
  }

  /** Prefetch inboxes used by rule conditions & rule actions */
  prefetch({companyUrl, namespace, cancelToken}: PrefetchProps) {
    return Promise.all([
      this.fetch({companyUrl, includePrivate: isTeammateNamespace(namespace), cancelToken}),
      this.fetch({companyUrl, namespace, cancelToken}),
    ]);
  }

  fetch({
    companyUrl,
    search = '',
    includePrivate = false,
    namespace,
    cancelToken,
  }: FetchProps): Promise<ReadonlyArray<InboxModel>> {
    const inboxesUrl = buildInboxSearchUrl(companyUrl, search, {
      namespace,
      shouldIncludePrivate: includePrivate,
      limit: InboxesLimit,
    });

    const cacheItem = this.cache.get(inboxesUrl);
    if (cacheItem) {
      return cacheItem;
    }

    const request = fetchApiAsync(ApiInboxes, inboxesUrl, {cancelToken});
    request.catch(() => this.cache.delete(inboxesUrl));
    this.cache.set(inboxesUrl, request);
    return request;
  }

  async fetchByIds({companyUrl, ids, cancelToken}: FetchIdsProps): Promise<ReadonlyArray<InboxModel>> {
    if (ids.length === 0) {
      return [];
    }

    const [cachedIds, uncachedIds] = partition(ids, (id) => this.byIdsCache.has(id));

    // paginate
    const chunks = chunk(uncachedIds, InboxesLimit);

    const uncachedRequest = Promise.all(
      chunks.map(
        (chunkIds) => fetchApiAsync(ApiInboxes, `${companyUrl}/inboxes/?ids=${chunkIds.join(',')}`),
        {
          cancelToken,
        },
      ),
    ).then(flatten);

    uncachedRequest.catch(() => uncachedIds.forEach((id) => this.byIdsCache.delete(id)));
    uncachedIds.forEach((id) => this.byIdsCache.set(id, uncachedRequest));

    return Promise.all([uncachedRequest, ...cachedIds.map((id) => this.byIdsCache.get(id)!)]).then(
      (requestsData) => {
        const idsSet = new Set(ids);
        return uniqBy(
          // Flatten data & only pick ids that we have requested
          flatten(requestsData).filter((inbox) => idsSet.has(inbox.id)),
          // One inbox per id
          'id',
        );
      },
    );
  }
}
